import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { getSiteOrigin, isIndexableDeployment } from "@/lib/seo/siteOrigin";
import { buildHead } from "@/lib/seo/headTags";
import { resolveSeoRoute } from "@/lib/seo/resolveSeoRoute";
import {
  getPageFaq,
  getPageCitations,
  getPageTool,
  getPageUpdated,
  getSeoPlans,
} from "@/lib/seo/sources";

/** React prop names for the few attributes that differ from HTML. */
const REACT_ATTR: Record<string, string> = { hreflang: "hrefLang" };

function toProps(attrs: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(attrs).map(([k, v]) => [REACT_ATTR[k] ?? k, v]));
}

/**
 * Route-aware `<head>`: title, description, robots, canonical, Open Graph,
 * Twitter, hreflang and JSON-LD. Built by `buildHead`, the same function the
 * prerender uses for the static HTML, so client navigation and the first
 * response agree.
 */
export function SeoHead() {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const route = resolveSeoRoute(pathname, language);
  const head = buildHead(route, {
    origin: getSiteOrigin(),
    deploymentIndexable: isIndexableDeployment(import.meta.env.VITE_VERCEL_ENV),
    plans: getSeoPlans(route.lang),
    faq: getPageFaq(route.pageKey, route.lang),
    tool: getPageTool(route.pageKey, route.lang),
    updated: getPageUpdated(route.pageKey),
    citations: getPageCitations(route.pageKey),
  });

  return (
    <Helmet htmlAttributes={{ lang: head.lang }}>
      <title>{head.title}</title>
      {head.tags.map(({ tag, attrs, content }) => {
        const key = `${tag}:${Object.values(attrs).slice(0, 2).join(":")}`;
        if (tag === "script") {
          return (
            <script key={key} {...toProps(attrs)}>
              {content}
            </script>
          );
        }
        const Tag = tag;
        return <Tag key={key} {...toProps(attrs)} />;
      })}
    </Helmet>
  );
}
